import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { StateOrName } from "@uirouter/react";
import classNames from "classnames";

import { DotsLoader, findParentByClass } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import {
    PlaceholderText,
    BoldPlaceholderText,
    AutocompleteStyled,
    ScrollAreaWrap,
} from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketingStyles";
import type { IRecentsResult } from "app/custom-hooks/useRecentsHook";
import { useRecents } from "app/custom-hooks/useRecentsHook";

import {
    generateAutocompleteSearchResultItems,
    generateAutocompleteRecentItems,
    generateAutocompleteDefaultItems,
    defaultWebsiteState,
    defaultKeywordState,
} from "./AutocompleteDigitalMarketingHelper";

interface IAutocompleteDigitalMarketingProps {
    /**
     * Used by the notouch homepages. Some homepages should/shouldn't include keywords data.
     */
    includeKeywordsData: boolean;
    maxCompareItemsToDisplay?: number;
    onAutocompleteOpen?: () => void;
    onAutocompleteClose?: () => void;
    websiteState?: string;
    onItemClick?: (key: string) => void;
    shouldNavigate?: (key: string) => boolean;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    RecentListSeparatorTextKey?: string;
    suggestedEntities?: string[];
    filterRecentsBy?: StateOrName | StateOrName[];
    excludeCompareRecents?: boolean;
    className?: string;
}

export const AutocompleteDigitalMarketing: React.FunctionComponent<IAutocompleteDigitalMarketingProps> = ({
    includeKeywordsData,
    maxCompareItemsToDisplay,
    onAutocompleteOpen = () => {},
    onAutocompleteClose = () => {},
    websiteState = undefined,
    onItemClick,
    shouldNavigate = () => true,
    RecentListSeparatorTextKey,
    filterRecentsBy,
    suggestedEntities,
    excludeCompareRecents = false,
    className,
}) => {
    const [recentWebsites, setRecentWebsites] = useState<IRecentsResult["recents"]>([]);
    const { recents: recentKeywords } = useRecents("keyword", true);
    const autocompleteRef = useRef(null);
    const handleBodyClick = (e: FixmeType) => {
        if (!findParentByClass(e.target, "AutocompleteDigitalMarketing")) {
            onAutocompleteClose();
            // @ts-ignore FIXME: strict migration - 'autocompleteRef.current' is possibly 'null' — untyped useRef(null); ref is assigned by AutocompleteStyled before this handler runs
            autocompleteRef.current.truncateResults(true);
        }
    };

    useEffect(() => {
        document.body.addEventListener("click", handleBodyClick, { capture: true });

        void (async () => {
            if (filterRecentsBy) {
                const recentWebsites = await getRecentsByParentState(filterRecentsBy, true);
                setRecentWebsites(recentWebsites);
            } else {
                const recentWebsites = await getRecentsAnalysis("website", true);
                setRecentWebsites(recentWebsites);
            }
        })();

        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, []);

    const getAutocompleteData = async (query: string) => {
        const hasQuery = query && query.length > 0;
        const filteredRecentWebsites = excludeCompareRecents
            ? recentWebsites.filter((item) => !item.data.comparedItems || item.data.comparedItems.length === 0)
            : recentWebsites;
        const hasRecent = filteredRecentWebsites.length || recentKeywords.length;
        return hasQuery
            ? await generateAutocompleteSearchResultItems(
                  query,
                  includeKeywordsData,
                  websiteState,
                  onItemClick,
                  shouldNavigate,
              )
            : !hasRecent
              ? await generateAutocompleteDefaultItems(
                    websiteState ?? defaultWebsiteState,
                    includeKeywordsData ? defaultKeywordState : undefined,
                    suggestedEntities,
                    onItemClick,
                    shouldNavigate,
                )
              : generateAutocompleteRecentItems(
                    filteredRecentWebsites,
                    includeKeywordsData ? recentKeywords : [],
                    websiteState,
                    maxCompareItemsToDisplay,
                    onItemClick,
                    shouldNavigate,
                    RecentListSeparatorTextKey,
                );
    };

    const SearchPlaceholder = useMemo(() => {
        return includeKeywordsData ? (
            <PlaceholderText>
                {translate("marketintelligence.companyresearch.websites.home.searchText")}{" "}
                <BoldPlaceholderText>
                    {translate("marketintelligence.companyresearch.websites.home.searchItem")}
                </BoldPlaceholderText>{" "}
                {translate("marketintelligence.companyresearch.websites.home.searchOr")}{" "}
                <BoldPlaceholderText>
                    {translate("marketintelligence.companyresearch.keyword.home.searchItem")}
                </BoldPlaceholderText>
            </PlaceholderText>
        ) : (
            <PlaceholderText>
                {translate("marketintelligence.companyresearch.websites.home.searchText")}{" "}
                {translate("marketintelligence.companyresearch.websites.home.searchItem")}
            </PlaceholderText>
        );
    }, [includeKeywordsData]);

    const renderAutocompleteItems = ({ listItems }: FixmeType) => {
        return <ScrollAreaWrap>{listItems}</ScrollAreaWrap>;
    };

    return (
        <AutocompleteStyled
            className={classNames("AutocompleteDigitalMarketing", className)}
            ref={autocompleteRef}
            placeholder={SearchPlaceholder}
            getListItems={getAutocompleteData}
            renderItems={renderAutocompleteItems}
            loadingComponent={<DotsLoader />}
            floating={true}
            debounce={250}
            preventTruncateUnlessForced={true}
            onFocus={onAutocompleteOpen}
        />
    );
};
